import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitStatus('loading');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      const res = await fetch('https://readdy.ai/api/form/d8sllvsjgj4v45gj7m0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <footer className="relative px-4 md:px-6 py-4">
      <div className="max-w-[1400px] mx-auto bg-foreground-950 rounded-3xl px-8 md:px-14 py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none select-none flex items-end justify-center pb-6 opacity-[0.04]">
          <span className="font-heading text-[180px] md:text-[240px] font-black text-background-50 leading-none whitespace-nowrap">
            MULEBUY
          </span>
        </div>

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          <div className="lg:col-span-1">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-background-50 leading-tight mb-6">
              Join the Spreadsheet Revolution
            </h3>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              Navigate
            </h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-sm text-background-300 hover:text-background-50 transition-colors">Home</Link></li>
              <li><Link to="/mulebuy-spreadsheet-guide" className="text-sm text-background-300 hover:text-background-50 transition-colors">Mulebuy Spreadsheet Guide</Link></li>
              <li><a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors">Mulebuy Spreadsheet Hub</a></li>
              <li>
                <a
                  href={categories[0]?.url || '#'}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-background-300 hover:text-background-50 transition-colors"
                >
                  Categories
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              Social
            </h4>
            <ul className="space-y-3">
              <li>
                <span className="flex items-center gap-3 text-sm text-background-300 group">
                  <i className="ri-instagram-line text-lg"></i>
                  Instagram
                </span>
              </li>
              <li>
                <span className="flex items-center gap-3 text-sm text-background-300 group">
                  <i className="ri-youtube-line text-lg"></i>
                  YouTube
                </span>
              </li>
              <li>
                <span className="flex items-center gap-3 text-sm text-background-300 group">
                  <i className="ri-facebook-line text-lg"></i>
                  Facebook
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-label text-sm font-semibold text-background-50 uppercase tracking-wider mb-5">
              Stay Updated
            </h4>
            <p className="text-sm text-background-300 mb-4 leading-relaxed">
              Get notified when new mulebuy spreadsheet lists drop.
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="flex items-center gap-0"
            >
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 bg-background-50/10 border border-background-50/20 rounded-l-xl px-4 py-3 text-sm text-background-50 placeholder:text-background-400 outline-none focus:border-accent-500 transition-colors"
              />
              <button
                type="submit"
                disabled={submitStatus === 'loading'}
                className="w-12 h-12 flex items-center justify-center bg-accent-500 rounded-r-xl hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-60"
              >
                {submitStatus === 'loading' ? (
                  <i className="ri-loader-4-line text-lg text-foreground-950 animate-spin"></i>
                ) : (
                  <i className="ri-arrow-right-line text-lg text-foreground-950"></i>
                )}
              </button>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-accent-400 mt-2">Subscribed! Check your inbox.</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400 mt-2">Something went wrong. Try again.</p>
            )}
          </div>
        </div>

        <div className="relative z-10 mt-14 pt-8 border-t border-background-50/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-background-400">
              &copy; 2026 MulebuySpreadsheets. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-xs text-background-400">Privacy Policy</span>
              <span className="text-xs text-background-400">Terms of Service</span>
              <span className="text-xs text-background-400">Disclaimer</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}